import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Circle,
  Clock,
  Moon,
  Sun,
  ChevronDown,
  ChevronUp,
  Loader2,
  Globe,
  CalendarDays,
  ShieldOff,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const POLL_INTERVAL_MS = 60 * 1000;

const parseUTC = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso);
  } catch (e) {
    return null;
  }
};

const fmtClock = (date, useUTC) => {
  if (!date) return "—";
  if (useUTC) {
    const h = String(date.getUTCHours()).padStart(2, "0");
    const m = String(date.getUTCMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  }
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const fmtDayLabel = (date, useUTC) => {
  if (!date) return "—";
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const d0 = useUTC
    ? new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    : new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dt0 = useUTC
    ? new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
    : new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diff = Math.round((dt0 - d0) / dayMs);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: useUTC ? "UTC" : undefined,
  });
};

const fmtDuration = (ms) => {
  const totalMin = Math.max(0, Math.round(ms / 60000));
  if (totalMin < 1) return "less than a minute";
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
};

// ============================================================================
// 48 h horizontal strip — striped fill to communicate sleep/unavailable
// ============================================================================
const ScheduleStrip = ({ slots, hoursAhead, useUTC, serverNow }) => {
  const startMs = serverNow ? serverNow.getTime() : Date.now();
  const cells = [];
  for (let h = 0; h < hoursAhead; h++) {
    const cellStart = new Date(startMs + h * 3600 * 1000);
    const cellEnd = new Date(cellStart.getTime() + 3600 * 1000);
    let type = "none";
    for (const s of slots || []) {
      const sStart = parseUTC(s.start_utc);
      const sEnd = parseUTC(s.end_utc);
      if (!sStart || !sEnd) continue;
      if (sStart < cellEnd && sEnd > cellStart) {
        // Blockers always win (sleep / unavailable) over available
        if (s.type === "sleep" || s.type === "unavailable") {
          type = s.type;
          break;
        }
        if (s.type === "available" && type === "none") {
          type = "available";
        }
      }
    }
    cells.push({ idx: h, start: cellStart, end: cellEnd, type });
  }

  const styleFor = (type) => {
    switch (type) {
      case "available":
        return { background: "#10b981" }; // emerald
      case "sleep":
        return {
          background:
            "repeating-linear-gradient(45deg, #4338ca 0 6px, #3730a3 6px 12px)",
        };
      case "unavailable":
        return {
          background:
            "repeating-linear-gradient(45deg, #b91c1c 0 6px, #7f1d1d 6px 12px)",
        };
      default:
        return { background: "#2a180a" };
    }
  };

  return (
    <div className="space-y-1.5" data-testid="schedule-strip">
      <div className="flex h-9 rounded-md overflow-hidden border-2 border-[#5a3a14] relative">
        {cells.map((c) => (
          <div
            key={c.idx}
            className="flex-1 relative group hover:brightness-125 transition"
            style={styleFor(c.type)}
            title={`${fmtDayLabel(c.start, useUTC)} ${fmtClock(c.start, useUTC)} — ${fmtClock(c.end, useUTC)}: ${
              c.type === "none" ? "owner offline / no schedule" : c.type
            }`}
          >
            {c.idx % 6 === 0 && c.idx > 0 && (
              <div className="absolute inset-y-0 left-0 w-px bg-black/40" />
            )}
          </div>
        ))}
        {/* "Now" marker at the very left */}
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-yellow-300 shadow-[0_0_4px_2px_rgba(253,224,71,0.6)] pointer-events-none" />
      </div>
      <div className="flex justify-between text-[10px] text-orange-200/70 font-mono px-0.5">
        {[0, 6, 12, 18, 24, 30, 36, 42, hoursAhead].map((h) => {
          if (h > hoursAhead) return null;
          const dt = new Date(startMs + h * 3600 * 1000);
          return (
            <div key={h} className="flex flex-col items-center">
              <span>+{h}h</span>
              <span className="text-orange-200/50">{fmtClock(dt, useUTC)}</span>
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-orange-200/80 pt-1">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={styleFor("available")} />
          <CheckCircle2 className="w-3 h-3 text-emerald-300" /> Available
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={styleFor("sleep")} />
          <Moon className="w-3 h-3 text-indigo-300" /> Sleeping
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={styleFor("unavailable")} />
          <ShieldOff className="w-3 h-3 text-red-300" /> Unavailable
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={styleFor("none")} />
          <Circle className="w-3 h-3 text-gray-400" /> No schedule (owner may still appear)
        </span>
        <span className="ml-auto flex items-center gap-1 text-yellow-300/90">
          <span className="inline-block w-1 h-3 bg-yellow-300 rounded-sm" /> Now
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// Per-day list of available windows
// ============================================================================
const ScheduleDetailList = ({ slots, useUTC }) => {
  const groups = useMemo(() => {
    const out = new Map();
    (slots || [])
      .filter((s) => s.type === "available")
      .forEach((s) => {
        const sStart = parseUTC(s.start_utc);
        const sEnd = parseUTC(s.end_utc);
        if (!sStart || !sEnd) return;
        const dayKey = useUTC
          ? `${sStart.getUTCFullYear()}-${String(sStart.getUTCMonth() + 1).padStart(2, "0")}-${String(sStart.getUTCDate()).padStart(2, "0")}`
          : `${sStart.getFullYear()}-${String(sStart.getMonth() + 1).padStart(2, "0")}-${String(sStart.getDate()).padStart(2, "0")}`;
        if (!out.has(dayKey)) out.set(dayKey, []);
        out.get(dayKey).push({ sStart, sEnd });
      });
    return Array.from(out.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [slots, useUTC]);

  if (groups.length === 0) {
    return (
      <div className="text-[12px] text-orange-200/60 italic">
        No available trading windows in the next 48 hours — feel free to leave an
        offer; it will be picked up when the owner returns.
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 gap-1.5" data-testid="schedule-detail-list">
      {groups.map(([key, list]) => {
        const dayDate = list[0].sStart;
        return (
          <div
            key={key}
            className="rounded-md border border-[#5a3a14] bg-[#1f1208] px-2 py-1.5"
          >
            <div className="text-[10px] uppercase tracking-wider font-bold text-orange-200/80 flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> {fmtDayLabel(dayDate, useUTC)}
            </div>
            <div className="text-xs font-mono text-emerald-300 mt-0.5">
              {list.map((w, i) => (
                <div key={i}>
                  {fmtClock(w.sStart, useUTC)} — {fmtClock(w.sEnd, useUTC)}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Big "what does this mean for me?" status banner
// ============================================================================
const StatusBanner = ({ data, useUTC }) => {
  const isOnline = !!data.is_online;
  const scheduledNow = !!data.is_scheduled_now;
  const blockerType = data.blocker_type;
  const blockerSlot = data.current_blocker_slot;
  const nextWin = data.next_available_window;

  const blockerEnd = blockerSlot ? parseUTC(blockerSlot.end_utc) : null;
  const nextStart = nextWin ? parseUTC(nextWin.start_utc) : null;
  const now = parseUTC(data.server_now_utc) || new Date();

  let theme = {
    bg: "bg-gray-700/60",
    border: "border-gray-500",
    text: "text-gray-100",
    icon: Circle,
  };
  let title = "Owner is currently offline";
  let description = "You can still leave an offer — it will be picked up when the owner returns.";
  let canTrade = false;
  let extra = null;

  if (isOnline) {
    theme = {
      bg: "bg-emerald-600/30",
      border: "border-emerald-400",
      text: "text-emerald-100",
      icon: CheckCircle2,
    };
    title = "Owner is ONLINE right now — trades can be completed";
    description = "The owner is actively present. Submit your offer and you should be able to meet at FM 15 — Channel 15 within minutes.";
    canTrade = true;
  } else if (blockerType === "sleep") {
    theme = {
      bg: "bg-indigo-700/40",
      border: "border-indigo-400",
      text: "text-indigo-100",
      icon: Moon,
    };
    title = "Owner is currently SLEEPING — trade cannot be completed now";
    description =
      "Your offer will sit safely in the queue. The owner will handle it after wake-up.";
    if (blockerEnd) {
      extra = (
        <span>
          Sleep ends around <strong className="font-mono">{fmtClock(blockerEnd, useUTC)}</strong> ({fmtDuration(blockerEnd - now)} from now).
        </span>
      );
    }
  } else if (blockerType === "unavailable") {
    theme = {
      bg: "bg-red-700/40",
      border: "border-red-400",
      text: "text-red-100",
      icon: ShieldOff,
    };
    title = "Owner is UNAVAILABLE — trade cannot be completed now";
    description =
      "The owner marked this time as unavailable (holiday, busy, etc.). Your offer is safe and will be processed later.";
    if (blockerEnd) {
      extra = (
        <span>
          Returns at <strong className="font-mono">{fmtClock(blockerEnd, useUTC)}</strong> ({fmtDuration(blockerEnd - now)} from now).
        </span>
      );
    }
  } else if (scheduledNow) {
    theme = {
      bg: "bg-amber-600/30",
      border: "border-amber-400",
      text: "text-amber-100",
      icon: AlertTriangle,
    };
    title = "Owner is usually available now, but seems AWAY";
    description =
      "This is a typical online window for the owner. They should be back shortly — leaving an offer is recommended.";
  }

  if (!canTrade && nextStart) {
    const inMs = nextStart - now;
    extra = (
      <div className="mt-1 text-[12px] font-bold">
        <Clock className="w-3.5 h-3.5 inline mr-1" />
        Next trade window:{" "}
        <span className="font-mono">
          {fmtDayLabel(nextStart, useUTC)} {fmtClock(nextStart, useUTC)}
        </span>{" "}
        <span className="opacity-80">(in {fmtDuration(inMs)})</span>
      </div>
    );
  }

  const Icon = theme.icon;

  return (
    <div
      className={`rounded-lg border-2 ${theme.border} ${theme.bg} ${theme.text} px-3 py-2.5 flex items-start gap-2.5`}
      data-testid="orestore-status-banner"
    >
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isOnline ? "animate-pulse" : ""}`} />
      <div className="flex-1 min-w-0">
        <div className="font-extrabold text-sm sm:text-base uppercase tracking-wide">
          {title}
        </div>
        <div className="text-[12px] sm:text-[13px] opacity-95 mt-0.5">
          {description}
        </div>
        {extra && (
          <div className="text-[12px] opacity-95 mt-1">{extra}</div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main widget
// ============================================================================
const OreStoreAvailabilityWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [useUTC, setUseUTC] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API_URL}/api/orestore/availability/status?hours=48`
      );
      setData(r.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="rounded-lg border-2 border-[#5a3a14] bg-[#1f1208] p-2.5 flex items-center gap-2 text-orange-200/70 text-sm mb-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking owner availability…
      </div>
    );
  }

  if (!data || !data.configured) {
    return null;
  }

  const isOnline = !!data.is_online;
  const scheduledNow = !!data.is_scheduled_now;
  const inBlocker = !!data.in_blocker;

  const tzLabel = useUTC
    ? "UTC"
    : Intl.DateTimeFormat().resolvedOptions().timeZone || "Local";

  // Pill in the header
  let pillLabel = "Offline";
  let pillClass = "bg-gray-500 text-gray-100";
  let PillIcon = Circle;
  if (isOnline) {
    pillLabel = "Online now";
    pillClass = "bg-emerald-500 text-white";
    PillIcon = Sun;
  } else if (data.blocker_type === "sleep") {
    pillLabel = "Sleeping";
    pillClass = "bg-indigo-500 text-white";
    PillIcon = Moon;
  } else if (data.blocker_type === "unavailable") {
    pillLabel = "Unavailable";
    pillClass = "bg-red-500 text-white";
    PillIcon = ShieldOff;
  } else if (scheduledNow) {
    pillLabel = "Away";
    pillClass = "bg-amber-500 text-amber-950";
    PillIcon = Clock;
  }

  return (
    <div
      className="rounded-lg border-4 border-[#5a3a14] bg-[#2a1a0e] shadow-[0_4px_0_#3b2509] overflow-hidden mb-4"
      data-testid="orestore-availability-widget"
    >
      <div className="flex items-center gap-2 px-3 py-2 bg-[#1f1208] border-b-2 border-[#5a3a14]">
        <div
          className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${pillClass} font-extrabold text-xs uppercase tracking-wider`}
          data-testid="online-status-pill"
        >
          <PillIcon className={`w-3.5 h-3.5 ${isOnline ? "animate-pulse" : ""}`} />
          {pillLabel}
        </div>
        <span className="text-orange-200 text-xs sm:text-sm hidden sm:inline">
          {data.admin_username ? <strong>{data.admin_username}</strong> : "Owner"} availability
        </span>
        <div className="ml-auto flex items-center gap-2 text-[11px] text-orange-200/80">
          <Globe className="w-3.5 h-3.5" />
          <button
            type="button"
            onClick={() => setUseUTC((v) => !v)}
            className="px-1.5 py-0.5 rounded border border-[#5a3a14] hover:border-orange-300 hover:text-orange-100 font-mono"
            data-testid="orestore-tz-toggle"
            title="Toggle between your local time and UTC"
          >
            {tzLabel}
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-[#5a3a14] hover:border-orange-300 hover:text-orange-100"
            data-testid="orestore-schedule-toggle"
          >
            {expanded ? "Hide" : "Schedule"}
            {expanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>

      <div className="p-3">
        <StatusBanner data={data} useUTC={useUTC} />
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          <div className="text-[11px] uppercase tracking-wider font-bold text-orange-200/80">
            Next 48 hours
          </div>
          <ScheduleStrip
            slots={data.slots}
            hoursAhead={data.horizon_hours || 48}
            useUTC={useUTC}
            serverNow={parseUTC(data.server_now_utc)}
          />
          <div className="text-[11px] uppercase tracking-wider font-bold text-orange-200/80 pt-2">
            Available trade windows ({tzLabel})
          </div>
          <ScheduleDetailList
            slots={data.slots}
            useUTC={useUTC}
            serverNow={parseUTC(data.server_now_utc)}
          />
        </div>
      )}
    </div>
  );
};

export default OreStoreAvailabilityWidget;
